<script setup lang="ts">
import {onMounted, type PropType, ref, watch} from 'vue';
import {
    type CabReportItem,
    enum_values,
    MijlpaalEnum,
    MijlpaalEnum_name,
    MijlpaalPrognoseEnum,
    MijlpaalPrognoseEnum_name,
    renderTerseDate,
    StatusEnum,
    StatusEnum_color,
    WaardeTypeEnum,
} from 'common';
import { downloadFile,jsonBodyFromResponse } from '@/base/utils.js';
import {getDocumentaryProof, updateDocumentaryProof, deleteDocumentaryProof, updateObject, readObject} from '@/base/callBackend';
import WaardeInvul from '@/views/general/WaardeInvul.vue';
import {ElInput, ElOption, ElSelect, ElMessage} from 'element-plus';
import StatusSelector from '@/views/general/StatusSelector.vue';
import WaardeFixed from '@/views/general/WaardeFixed.vue';
import UploadIcon from '../icons/UploadIcon.vue';
import DeleteIcon from '../icons/DeleteIcon.vue';

//===================================================================================================================
const props = defineProps({
    item: {
        type    : Object as PropType<CabReportItem>,
        required: true,
    },
    showOrgTheme: {
        type   : Boolean,
        default: false,
    },
});

const theValue            = ref<MijlpaalEnum | undefined>(props.item.reportedValue);
const theUitkeringsbedrag = ref(props.item.uitkeringsbedrag);
const thePrognose         = ref<number | MijlpaalPrognoseEnum | undefined>(props.item.prognose);
const statusTarget = ref<StatusEnum[]>([]);
const documentaryProofName = ref<string|undefined>(props.item.documentaryProofName);

//=================================================================================================
onMounted(async () => {
    statusTarget.value = [props.item.status!];
});

//=================================================================================================
watch(theValue, (v) => {
    reportedValueChanged(v);
});
watch(theUitkeringsbedrag, (v) => {
    uitkeringsbedragChanged(v);
});
watch(thePrognose, (v) => {
    prognoseChanged(v);
});
watch(statusTarget, async (nv, ov) => {
    if (ov[0]) {
        const newValue        = nv[0];
        const resultingStatus = await onStatusChange(newValue);
        if (resultingStatus !== newValue) {
            statusTarget.value = [resultingStatus];
        }
    }
});

//=================================================================================================
async function reportedValueChanged(v: number | MijlpaalEnum | undefined) {
    if (v !== theValue.value || v !== props.item.reportedValue) {
        theValue.value           = v;
        props.item.reportedValue = v;
        await onChange();
    }
}

async function prognoseChanged(v: number | MijlpaalPrognoseEnum | undefined) {
    if (v !== thePrognose.value || v !== props.item.prognose) {
        thePrognose.value   = v;
        props.item.prognose = v;
        await onChange();
    }
}

function documentaryProofChanged(name?: string) {
    if (name !== documentaryProofName.value || name !== props.item.documentaryProofName) {
        documentaryProofName.value         = name;
        props.item.documentaryProofName = name;
    }
}

function keyupOnTheValue(event: any) {
    if (event.key === 'Backspace' || event.key === 'Delete') {
        theValue.value = undefined;
        event.target.blur();
    }
}

function keyupOnThePrognose(event: any) {
    if (event.key === 'Backspace' || event.key === 'Delete') {
        thePrognose.value = undefined;
        event.target.blur();
    }
}

async function onChange() {
    if (props.item.reportedValue === MijlpaalEnum.gehaald || props.item.reportedValue === MijlpaalEnum.gestart) {
        props.item.mitigatie = '';
        thePrognose.value    = undefined;
        props.item.prognose  = undefined;
    }
    await updateObject(props.item);
}

async function uploadDocumentaryProof(uuid:string) {
    const input                 = document.createElement('input');
    input.type                  = 'file';
    input.style.display = 'none';
    input.accept = '.pdf';
    input.addEventListener('change', async (event: Event) => {
        const target = event.target as HTMLInputElement;
        const file   = target.files?.[0] ?? null;
        if (file) {
            const formData = new FormData();
            formData.append('file', file);
            const res  = await updateDocumentaryProof(uuid, formData);
            const body = await jsonBodyFromResponse(res);
            if (res.ok) {
                ElMessage({
                    message : `Bewijsstuk ${documentaryProofName.value?'vervangen':'toegevoegd'}.`,
                    type    : 'success',
                    duration: 10000,
                });
                documentaryProofChanged(file.name);
            } else {
                console.error(body.message);
                ElMessage({
                    message : `Upload mislukt: (${body.message})`,
                    type    : 'error',
                    duration: 10000,
                });
            }
        }
        document.body.removeChild(input);
    });
    document.body.appendChild(input);
    input.click();

}

//===================================================================================================================
async function uitkeringsbedragChanged(newValue?: number) {
    props.item.uitkeringsbedrag = newValue ?? 0;
    await onChange();
}

async function onStatusChange(newStatus: StatusEnum): Promise<StatusEnum> {
    props.item.status = newStatus;
    await onChange();
    const newItem     = await readObject(props.item);
    props.item.status = newItem.status;
    return newItem.status!;
}

//===================================================================================================================
function dynColor() {
    return 'bg-' + StatusEnum_color(props.item.status);
}

function labelClass(first: boolean) {
    const w = first ? 'w-44' : 'w-28';
    return `text-xs text-right ${w} mr-2`;
}

function renderMoment() {
    return renderTerseDate(props.item.meetMoment!);
}
</script>

<template>
    <div class="flex flex-row items-start" :class="dynColor()">

        <!--DATE-->
        <div class="backdrop-brightness-90 font-extrabold text-center my-1 mx-1 w-32">
            {{ renderMoment() }}
        </div>

        <div class="flex flex-col w-full my-1 pr-4">

            <div class="flex flex-row font-bold text-gray-700 text-xl w-full pb-3" v-if="props.showOrgTheme">
                <div class="px-3">
                    {{ item.organization[0].name }}
                </div>
                -
                <div class="px-3">
                    {{ item.theme[0].name }}
                </div>
            </div>

            <!--ROW -->
            <div class="flex flex-row items-center w-full my-1">

                <!-- gerapporteerde waarde-->
                <div class="flex flex-row items-center"
                     v-if="item.showReportedValue()">
                    <div :class="labelClass(true)">
                        gerapporteerde waarde
                    </div>
                    <div class="font-bold">
                        <el-select
                            v-model="theValue"
                            placeholder="kies een optie"
                            style="min-width: 270px"
                            @keyup="keyupOnTheValue($event)"
                        >
                            <el-option
                                v-for="mp in enum_values(MijlpaalEnum)"
                                :key="mp"
                                :label="MijlpaalEnum_name(mp)"
                                :value="mp"
                            />
                        </el-select>
                    </div>
                </div>
                <div class="flex flex-row items-center"
                     v-else>
                    <div :class="labelClass(true)">
                    </div>
                    <div class="text-sm text-gray-400" style="min-width: 270px">
                        geen waarde, alleen prognose
                    </div>
                </div>

                <!-- prognose-->
                <div class="flex flex-row items-center"
                     v-if="item.showPrognose()">
                    <div :class="labelClass(false)">
                        prognose
                    </div>
                    <div class="font-bold">
                        <div class="">
                            <el-select
                                v-model="thePrognose"
                                placeholder="kies een optie"
                                style="min-width: 270px"
                                @keyup="keyupOnThePrognose($event)"
                            >
                                <el-option
                                    v-for="mp in enum_values(MijlpaalPrognoseEnum)"
                                    :key="mp"
                                    :label="MijlpaalPrognoseEnum_name(mp)"
                                    :value="mp"
                                />
                            </el-select>
                        </div>
                    </div>
                </div>
            </div>

            <!--ROW: bewijsstuk-->
            <div class="flex flex-row items-center w-full my-1" v-if="item.showDocumentaryProof(true)">
                <div class="flex flex-row items-center">
                    <div :class="labelClass(true)">
                        bewijsstuk
                    </div>
                    <div class="bg-white/40 px-2 py-1">
                        <a v-if="documentaryProofName"
                            href="#"
                            @click.prevent="getDocumentaryProof(item.uuid).then((res)=>downloadFile(res, documentaryProofName!))"
                            class="text-blue-500 hover:text-blue-700">
                                {{documentaryProofName}}
                        </a>
                        <span v-else>none</span>
                    </div>
                    <DeleteIcon v-if="documentaryProofName"
                        @click.prevent="deleteDocumentaryProof(item.uuid).then(() => documentaryProofChanged(undefined))">
                    </DeleteIcon>
                    <UploadIcon
                        :title="documentaryProofName?'vervang':'bijvoegen'"
                        @click.prevent="uploadDocumentaryProof(item.uuid)">
                    </UploadIcon>
                </div>
            </div>

            <!--ROW: uitkering/vecozo-->
            <div class="flex flex-col items-center w-full my-1 border border-gray-400 py-2"
                 v-if="item.showUitkeringsbedrag(true) || item.showVecozoId(true)">

                <div class="relative w-full">
                    <div class="absolute left-3 -top-4 px-2 whitespace-nowrap text-xs" :class="dynColor()">
                        uitkering
                    </div>
                </div>

                <!--ROW: uitkering/vecozo-->
                <div class="flex flex-row items-center w-full my-1"
                     v-if="item.showUitkeringsbedrag(true) || item.showVecozoId(true)">

                    <!--uitkeringsbedrag-->
                    <div class="flex flex-row items-center"
                         v-if="item.showUitkeringsbedrag(true)">
                        <div :class="labelClass(true)">
                            uitkeringsbedrag
                        </div>

                        <div class="font-bold">
                            <WaardeInvul
                                v-model:waarde="theUitkeringsbedrag"
                                :waarde-type="WaardeTypeEnum.bedrag"
                            />
                        </div>
                    </div>

                    <!--vecozo id-->
                    <div class="flex flex-row items-center"
                         v-if="item.showVecozoId(true)">
                        <div :class="labelClass(false)">
                            vecozo id
                        </div>
                        <div class="font-bold">
                            <input
                                class="px-2 py-1 border-1 w-full"
                                v-model="item.vecozoId"
                                type="text"
                                style="min-width: 270px"
                                @input="onChange"
                            />
                        </div>
                    </div>

                </div>

                <!--ROW: uitkerings details-->
                <div class="flex flex-row items-center w-full my-1"
                     v-if="item.uitkeringsbedrag && item.vecozoId">

                    <div class="flex flex-row items-center">
                        <div :class="labelClass(true)">
                            vooraf (tegoed/uitgekeerd)
                        </div>
                        <WaardeFixed
                            v-model:waarde="item.nogUitTeKerenVoorToekenningBedrag"
                            :waarde-type="WaardeTypeEnum.bedrag"
                        />
                        /
                        <WaardeFixed
                            v-model:waarde="item.alUitgekeerdVoorToekenningBedrag"
                            :waarde-type="WaardeTypeEnum.bedrag"
                        />
                    </div>
                    <div class="flex flex-row items-center">
                        <div :class="labelClass(false)">
                            aangevraagd
                        </div>
                        <WaardeFixed
                            v-model:waarde="item.aangevraagdBedrag"
                            :waarde-type="WaardeTypeEnum.bedrag"
                        />
                    </div>
                    <div class="flex flex-row items-center">
                        <div :class="labelClass(false)">
                            toegekend
                        </div>
                        <WaardeFixed
                            v-model:waarde="item.toegekendBedrag"
                            :waarde-type="WaardeTypeEnum.bedrag"
                        />
                    </div>
                    <div class="flex flex-row items-center">
                        <div :class="labelClass(true)">
                            na (tegoed/uitgekeerd)
                        </div>
                        <WaardeFixed
                            v-model:waarde="item.nogUitTeKerenNaToekenningBedrag"
                            :waarde-type="WaardeTypeEnum.bedrag"
                        />
                        /
                        <WaardeFixed
                            v-model:waarde="item.alUitgekeerdNaToekenningBedrag"
                            :waarde-type="WaardeTypeEnum.bedrag"
                        />
                    </div>
                </div>
            </div>

            <!--ROW: opmerking-->
            <div class="flex flex-row items-center w-full my-1">
                <div :class="labelClass(true)">
                    opmerking
                </div>

                <div class="flex-1"
                     title="opmerking">
                    <el-input
                        v-model="item.opmerking"
                        @input="onChange()"
                        type="textarea"
                        :autosize="{ minRows: 1, maxRows: 5 }"
                    />
                </div>
            </div>

            <!--ROW: mitigatie-->
            <div class="flex flex-row items-center w-full my-1"
                 v-if="item.showMitigatie()">
                <div :class="labelClass(true)">
                    mitigatie
                </div>

                <div class="flex-1"
                     title="mitigatie">
                    <el-input
                        v-model="item.mitigatie"
                        @input="onChange()"
                        type="textarea"
                        :autosize="{ minRows: 1, maxRows: 5 }"
                    />
                </div>
            </div>

        </div>

        <div class="m-2">
            <StatusSelector :multi="false"
                            v-model:statusSet="statusTarget"
            />
        </div>
    </div>
</template>
